import _ from 'underscore';
import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses } from '@fluentui/react-components';
import {
  AdsStudioSpinner,
  GenerateImageContext,
  CSMLSortBy,
  AIGCHandler,
  AdStudioSurveyTriggerFlag,
  addAdStudioSurveyTriggerFlag,
  RealUsedFlagSuffix,
  handleRealUsedFlag,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import { HomePageTopBannerComponent, AssetListBarCarousel, HomePageGenerateWithAIComponent } from './components';
import { ActionAssetCards } from './components/home-page-asset-cards/action-asset-cards';
import { ImageBackgroundCards } from './components/home-page-image-background-section';
import { useHomePageStyle } from './home-page-styles';

const HomePage = ({
  i18n,
  aid,
  cid,
  oDataConfig,
  dataService,
  libraryDataService,
  notificationApi,
  isAdsStudioAIGCMVPEnabled,
  isAIGCRegionRestricted,
  logger,
  reload,
  navigation,
  pagePerfMarker: homePagePerfMarker,
  setDataLoaded,
  permissions,
  adsStudioAccountPreferences,
}) => {
  const classes = useHomePageStyle();

  const {
    loadData,
    assets = [],
    renameAsset,
  } = dataService;

  const {
    navigateIntoFolderItem,
  } = libraryDataService;

  const [isPageInited, setPageInited] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const { setGenerateImageExample } = useContext(GenerateImageContext);
  const isAdsStudioBackgroundChangeEnabled = _.get(permissions, ['dynamic', 'AdsStudioBackgroundChange'], false);

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load images. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  // init page
  useEffect(() => {
    if (!isPageInited) {
      setPageInited(true);
    }
  }, [isPageInited]);

  usePageActiveTimeTracker(logger, 'Home');

  useEffect(() => {
    setClarityTag({ LoadAdsStudioHomePage: true });
  }, []);

  // load data for initial page load
  useEffect(() => {
    if (!isPageInited) {
      // wait until page is initialized
      return;
    }

    // Important! Add a trivial log so that reload will be an explicit dep.
    if (reload) {
      logger.logTrace('force to reload');
    }

    homePagePerfMarker.willFetchData();
    setLoadingData(true);
    loadData({
      isGlobalSearch: true,
      assetsToLoadForSubfolders: 5,
      includeParentHierarchy: true,
      isRecentSorting: true,
      sortBy: CSMLSortBy.MostRecent,
    })
      .catch(() => {
        onFailedToLoadData();
      }).finally(() => {
        setLoadingData(false);
        homePagePerfMarker.willRender();
        if (setDataLoaded) {
          setDataLoaded();
        }
        addAdStudioSurveyTriggerFlag(adsStudioAccountPreferences, AdStudioSurveyTriggerFlag.App, RealUsedFlagSuffix.App);
      });
  }, [isPageInited, loadData, onFailedToLoadData, reload, logger]);

  const isPageLoading = !isPageInited || loadingData;

  const onSearch = useCallback((searchQuery) => {
    navigation.gotoSearchResultPage({
      query: searchQuery,
    });
  }, [navigation]);

  const onGenerateImage = useCallback(({ url, text }) => {
    handleRealUsedFlag(adsStudioAccountPreferences, RealUsedFlagSuffix.App);
    AIGCHandler(isAdsStudioAIGCMVPEnabled, isAIGCRegionRestricted, notificationApi, i18n, () => {
      setGenerateImageExample({
        prompt: text,
        imageUrl: url,
      });
      navigation.gotoAIGenerationPanelPage();
    });
  }, [adsStudioAccountPreferences, i18n, isAIGCRegionRestricted, isAdsStudioAIGCMVPEnabled, navigation, notificationApi, setGenerateImageExample]);

  const sortedAssets = useMemo(() => (_.sortBy(assets, asset => asset.modifiedTime).reverse()), [assets]);

  return (
    <div className={mergeClasses('home-page-container', classes.homePage)}>
      {(isPageLoading) && (
        <Overlay>
          <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 250 }} />
        </Overlay>
      )}
      <HomePageTopBannerComponent
        i18n={i18n}
        onSearch={onSearch}
      />

      <ActionAssetCards i18n={i18n} aid={aid} cid={cid} logger={logger} oDataConfig={oDataConfig} navigation={navigation} adsStudioAccountPreferences={adsStudioAccountPreferences} permissions={permissions} />

      {
        !isPageLoading &&
        <AssetListBarCarousel
          assets={sortedAssets}
          renameAsset={renameAsset}
          i18n={i18n}
          navigation={navigation}
          navigateIntoFolderItem={navigateIntoFolderItem}
          logger={logger}
          adsStudioAccountPreferences={adsStudioAccountPreferences}
        />
      }

      {
        isAdsStudioBackgroundChangeEnabled &&
        <ImageBackgroundCards
          aid={aid}
          cid={cid}
          oDataConfig={oDataConfig}
          i18n={i18n}
          navigation={navigation}
          loadingStatus={isPageLoading}
          isAIGCRegionRestricted={isAIGCRegionRestricted}
          notificationApi={notificationApi}
          adsStudioAccountPreferences={adsStudioAccountPreferences}
        />
      }

      <HomePageGenerateWithAIComponent i18n={i18n} onGenerateImage={onGenerateImage} loadingStatus={isPageLoading} />
    </div>
  );
};

HomePage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  dataService: PropTypes.shape({
    assets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    loadData: PropTypes.func.isRequired,
    renameAsset: PropTypes.func.isRequired,
  }),
  libraryDataService: PropTypes.shape({
    navigateIntoFolderItem: PropTypes.func,
  }),
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func.isRequired,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  adsStudioAccountPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  navigation: PropTypes.shape({
    gotoSearchResultPage: PropTypes.func.isRequired,
    gotoAIGenerationPanelPage: PropTypes.func.isRequired,
  }).isRequired,
  isAIGCRegionRestricted: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}).isRequired,
  }).isRequired,
};

HomePage.defaultProps = {
  dataService: {},
  libraryDataService: {},
  notificationApi: {},
  adsStudioAccountPreferences: undefined,
  reload: null,
  isAIGCRegionRestricted: false,
};

export const HomePageComponent = HomePage;
